class HomepageSlideMenu extends HTMLElement {
    constructor() {
        super();
        this.triggers = [];
        this.menuContents = [];
        this.header = null;
        this.closeBtn = null;
        this._raf = null;
        this.overlay = null;
        this.hamburgerBtn = null;
        this.isMobile = false;

        // funciones enlazadas
        this.onScrollOrResize = this.onScrollOrResize.bind(this);
        this.onMouseLeave = this.onMouseLeave.bind(this);
        this.onHamburgerClick = this.onHamburgerClick.bind(this);
        this.checkIfMobile = this.checkIfMobile.bind(this);
        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onMenuItemClick = this.onMenuItemClick.bind(this);
    }

    connectedCallback() {
        // Buscar elementos una vez que el nodo existe en el DOM
        this.triggers = document.querySelectorAll('.homepage-option-wrapper');
        this.menuContents = this.querySelectorAll('.menu-content');
        this.header = document.querySelector('.header-wrapper');
        this.overlay = document.querySelector('.menu-overlay');
        this.hamburgerBtn = document.querySelector('.header--homepage-options #hamburger-menu-btn');

        // Botón cerrar accesible (opcional)
        if (!this.querySelector('.homepage-slide-menu__close')) {
            this.closeBtn = document.createElement('button');
            this.closeBtn.type = 'button';
            this.closeBtn.className = 'homepage-slide-menu__close';
            this.closeBtn.setAttribute('aria-label', 'Cerrar menú');
            this.closeBtn.textContent = '×';
            this.closeBtn.addEventListener('click', () => this.closeMenu());
            this.appendChild(this.closeBtn);
        } else {
            this.closeBtn = this.querySelector('.homepage-slide-menu__close');
            this.closeBtn.addEventListener('click', () => this.closeMenu());
        }

        // Añadir evento de clic al overlay para cerrar el menú
        if (this.overlay) {
            this.overlay.addEventListener('click', () => this.closeMenu());
        }

        // Agregar evento de clic al botón hamburguesa
        if (this.hamburgerBtn) {
            this.hamburgerBtn.addEventListener('click', this.onHamburgerClick);
        }

        // Detectar si es móvil y configurar eventos apropiados
        this.checkIfMobile();
        this.setupEventListeners();

        // Cerrar al salir del contenedor del mega menú
        this.addEventListener('mouseleave', this.onMouseLeave);
        
        // Escuchar cambios en el tamaño de la ventana
        window.addEventListener('resize', this.checkIfMobile);
    }

    disconnectedCallback() {
        this.removeAllEventListeners();
        window.removeEventListener('resize', this.checkIfMobile);
        window.removeEventListener('scroll', this.onScrollOrResize);
        window.removeEventListener('resize', this.onScrollOrResize);
        
        if (this.overlay) {
            this.overlay.removeEventListener('click', () => this.closeMenu());
        }

        if (this.hamburgerBtn) {
            this.hamburgerBtn.removeEventListener('click', this.onHamburgerClick);
        }
        
        this.removeEventListener('mouseleave', this.onMouseLeave);
    }

    checkIfMobile() {
        // Detectar si es móvil (ancho menor a 990px)
        this.isMobile = window.innerWidth < 990;
        this.setupEventListeners();
    }

    setupEventListeners() {
        // Remover todos los eventos primero
        this.removeAllEventListeners();

        if (this.isMobile) {
            // En móvil: usar clics
            this.triggers.forEach(trigger => {
                trigger.addEventListener('click', this.onTriggerClick);
            });
        } else {
            // En desktop: usar hover
            this.triggers.forEach(trigger => {
                trigger.addEventListener('mouseenter', this.onTriggerEnter);
            });
        }
    }

    removeAllEventListeners() {
        this.triggers.forEach(trigger => {
            trigger.removeEventListener('mouseenter', this.onTriggerEnter);
            trigger.removeEventListener('click', this.onTriggerClick);
        });
    }

    onHamburgerClick(event) {
        event.preventDefault();
        event.stopPropagation();
        
        // Si el menú está abierto, cerrarlo
        if (this.classList.contains('is-open')) {
            this.closeMenu();
            return;
        }

        const triggersWithMenu = Array.from(this.triggers).filter(t => t.dataset.hasMenu === 'true');

        if (!triggersWithMenu.length) {
            this.openMenu();
            return;
        }

        // Preferir la opción activa en la página, pero solo si tiene menú (ej. PLAYA no tiene)
        let targetTrigger = Array.from(this.triggers).find(
            t => t.classList.contains('selected') && t.dataset.hasMenu === 'true'
        );
        if (!targetTrigger) {
            targetTrigger = triggersWithMenu[0];
        }

        this.triggers.forEach(t => t.classList.remove('active'));
        targetTrigger.classList.add('active');
        this.showMenu(targetTrigger.dataset.menuHandle);
    }

    onMouseLeave() {
        // Solo cerrar en desktop, no en móvil
        if (!this.isMobile) {
            this.closeMenu();
        }
    }

    onTriggerClick = (event) => {
        const trigger = event.currentTarget;

        // Sin menú configurado (ej. PLAYA): dejar que el enlace navegue directamente
        if (trigger.dataset.hasMenu !== 'true') {
            return;
        }

        event.preventDefault();
        event.stopPropagation();

        this.triggers.forEach(t => t.classList.remove('active'));
        trigger.classList.add('active');

        const handle = trigger.dataset.menuHandle;
        this.showMenu(handle);
    };

    onTriggerEnter = (event) => {
        const trigger = event.currentTarget;
        this.triggers.forEach(t => t.classList.remove('active'));
        trigger.classList.add('active');

        const handle = trigger.dataset.menuHandle;
        this.showMenu(handle);
    };

    onMenuItemClick = (event) => {
        const item = event.currentTarget;
        
        // Solo prevenir redirección en móvil si tiene hijos
        if (this.isMobile && item.hasAttribute('has-grandchildren')) {
            event.preventDefault();
            event.stopPropagation();
            
            // Simular hover para mostrar submenú
            const mouseEnterEvent = new MouseEvent('mouseenter', {
                view: window,
                bubbles: true,
                cancelable: true
            });
            item.dispatchEvent(mouseEnterEvent);
        }
    };

    showMenu(handle) {
        // ocultar todo
        this.menuContents.forEach(c => c.classList.add('hidden'));
        this.querySelectorAll('.sub-menu-content').forEach(s => s.classList.add('hidden'));
        this.querySelectorAll('.header__menu-item').forEach(i => i.classList.remove('active'));
        this.querySelectorAll('.grandchild-menu').forEach(g => g.classList.remove('active'));
        this.querySelectorAll('.grandchild-container').forEach(gc => gc.classList.add('hidden'));

        const target = this.querySelector(`.menu-content[data-menu-handle="${handle}"]`);
        if (!target) return;

        target.classList.remove('hidden');

        // Reunir el grupo de submenús contiguos a este bloque
        const subMenuGroup = [];
        let sib = target.nextElementSibling;
        while (sib && !sib.matches('.menu-content')) {
            if (sib.matches('.sub-menu-content')) subMenuGroup.push(sib);
            sib = sib.nextElementSibling;
        }

        // Quitar listeners antiguos si los hubiera
        if (target._submenuListeners) {
            target._submenuListeners.forEach(({ el, fn }) => el.removeEventListener('mouseenter', fn));
        }
        target._submenuListeners = [];

        // Estado base de ancho (sin submenú/ni nietos aún)
        this.classList.remove('has-submenu', 'has-grandchildren');

        // Configurar eventos para los items del menú
        const items = Array.from(target.querySelectorAll('.flex-row .header__menu-item'));
        items.forEach((item, idx) => {
            // Remover eventos anteriores
            item.removeEventListener('click', this.onMenuItemClick);
            
            // Agregar evento de clic en móvil para items con hijos
            if (this.isMobile && item.hasAttribute('has-grandchildren')) {
                item.addEventListener('click', this.onMenuItemClick);
            }

            const fn = () => {
                items.forEach(i => i.classList.remove('active'));
                item.classList.add('active');

                // ocultar todos los submenús y su columna de nietos
                subMenuGroup.forEach(sm => {
                    sm.classList.add('hidden');
                    const gc = sm.querySelector('.grandchild-container');
                    if (gc) gc.classList.add('hidden');
                    sm.querySelectorAll('.grandchild-menu').forEach(g => g.classList.remove('active'));
                    sm.querySelectorAll('.childlink-item').forEach(cl => cl.classList.remove('active'));
                });

                // mostrar el submenú correspondiente (si existe)
                const sm = subMenuGroup[idx];
                // al cambiar de item, volvemos a estado sin nietos
                this.classList.remove('has-grandchildren');

                if (sm) {
                    sm.classList.remove('hidden');
                    // activar estado con submenú para transicionar ancho
                    this.classList.add('has-submenu');
                    this.setupChildLinkEvents(sm);
                } else {
                    // si este item no tiene submenú, colapsar al ancho base
                    this.classList.remove('has-submenu');
                }
            };
            
            // En móvil, usar clic en lugar de hover para items con hijos
            if (this.isMobile && item.hasAttribute('has-grandchildren')) {
                item.addEventListener('click', fn);
            } else {
                item.addEventListener('mouseenter', fn);
            }
            
            target._submenuListeners.push({ el: item, fn });
        });

        this.openMenu();
    }

    setupChildLinkEvents(submenuContent) {
        if (submenuContent._childLinkListeners) {
            submenuContent._childLinkListeners.forEach(({ el, fn }) => el.removeEventListener('mouseenter', fn));
            submenuContent._childLinkListeners.forEach(({ el, fn }) => el.removeEventListener('click', fn));
        }
        submenuContent._childLinkListeners = [];

        const childLinks = Array.from(submenuContent.querySelectorAll('.childlink-item'));
        const grandchildContainer = submenuContent.querySelector('.grandchild-container');
        const grandchildMenus = Array.from(submenuContent.querySelectorAll('.grandchild-menu'));
    
        if (grandchildContainer) {
            grandchildContainer.classList.add('hidden');
            // En móvil, también remover la clase show si existe
            if (this.isMobile) {
                grandchildContainer.classList.remove('show');
            }
        }
        grandchildMenus.forEach(menu => menu.classList.remove('active'));
        childLinks.forEach(cl => cl.classList.remove('active'));
    
        // Botón "Volver" en móvil para esconder el grandchild-container activo
        if (this.isMobile && grandchildContainer && !grandchildContainer.querySelector('.grandchild-close-btn')) {
            const backBtn = document.createElement('button');
            backBtn.type = 'button';
            backBtn.className = 'grandchild-close-btn';
            backBtn.textContent = 'Volver';
            backBtn.addEventListener('click', (event) => {
                event.preventDefault();
                event.stopPropagation();
    
                // Ocultar contenedor de nietos y resetear estado
                grandchildContainer.classList.add('hidden');
                grandchildContainer.classList.remove('show');
                grandchildMenus.forEach(menu => menu.classList.remove('active'));
                childLinks.forEach(cl => cl.classList.remove('active'));
                this.classList.remove('has-grandchildren');
            });
            // Inserta el botón al principio del grandchild-container
            grandchildContainer.insertBefore(backBtn, grandchildContainer.firstChild);
        }
    
        childLinks.forEach((childLink, idx) => {
            const fn = (event) => {
                // Prevenir redirección en móvil si tiene hijos
                if (this.isMobile && childLink.dataset.hasChildren === 'true') {
                    event.preventDefault();
                    event.stopPropagation();
                }
    
                childLinks.forEach(cl => cl.classList.remove('active'));
                childLink.classList.add('active');
    
                const hasChildren = childLink.dataset.hasChildren === 'true';
    
                grandchildMenus.forEach(menu => menu.classList.remove('active'));
    
                if (hasChildren) {
                    if (grandchildContainer) {
                        grandchildContainer.classList.remove('hidden');
                        // En móvil, agregar la clase show para la animación
                        if (this.isMobile) {
                            grandchildContainer.classList.add('show');
                        }
                    }
                    const targetMenu = submenuContent.querySelector(`.grandchild-menu[data-parent-index="${idx}"]`);
                    if (targetMenu) targetMenu.classList.add('active');
                    // activar estado con nietos para ampliar ancho con transición
                    this.classList.add('has-grandchildren');
                } else {
                    if (grandchildContainer) {
                        grandchildContainer.classList.add('hidden');
                        // En móvil, remover la clase show
                        if (this.isMobile) {
                            grandchildContainer.classList.remove('show');
                        }
                    }
                    // colapsar ancho si el hijo no tiene nietos
                    this.classList.remove('has-grandchildren');
                }
            };
            
            // En móvil, usar clic en lugar de hover para todos los childlink-item
            if (this.isMobile) {
                childLink.addEventListener('click', fn);
            } else {
                childLink.addEventListener('mouseenter', fn);
            }
            
            submenuContent._childLinkListeners.push({ el: childLink, fn });
        });
    }

    // Recalcula la posición y altura del panel en función del header
    updateLayout = () => {
        let offset = 0;
        this.style.setProperty('--drawer-top', `${offset}px`);
        this.style.setProperty('--drawer-height', `calc(100vh - ${offset}px)`);
        
        // Actualizar también el overlay
        if (this.overlay) {
            this.overlay.style.setProperty('--drawer-top', `${offset}px`);
            this.overlay.style.setProperty('--drawer-height', `calc(100vh - ${offset}px)`);
        }
    };

    onScrollOrResize() {
        if (!this.classList.contains('is-open')) return;
        if (this._raf) cancelAnimationFrame(this._raf);
        this._raf = requestAnimationFrame(this.updateLayout);
    }

    openMenu() {
        this.classList.add('is-open');
        // al abrir, partimos del ancho base
        this.classList.remove('has-submenu', 'has-grandchildren');
        this.updateLayout();
        
        // Activar el overlay
        if (this.overlay) {
            this.overlay.classList.remove('hidden');
            this.overlay.classList.add('active');
        }
        
        window.addEventListener('scroll', this.onScrollOrResize, { passive: true });
        window.addEventListener('resize', this.onScrollOrResize, { passive: true });
    }

    closeMenu() {
        this.classList.remove('is-open');
        // resetear estados de ancho
        this.classList.remove('has-submenu', 'has-grandchildren');
        this.triggers.forEach(t => t.classList.remove('active'));
        this.querySelectorAll('.header__menu-item').forEach(i => i.classList.remove('active'));
        this.querySelectorAll('.grandchild-menu').forEach(g => g.classList.remove('active'));
        this.querySelectorAll('.grandchild-container').forEach(gc => gc.classList.add('hidden'));
        
        // Desactivar el overlay
        if (this.overlay) {
            this.overlay.classList.remove('active');
            setTimeout(() => {
                if (!this.classList.contains('is-open')) {
                    this.overlay.classList.add('hidden');
                }
            }, 320); // Mismo tiempo que la transición
        }

        window.removeEventListener('scroll', this.onScrollOrResize);
        window.removeEventListener('resize', this.onScrollOrResize);
    }
}

if (!customElements.get('homepage-slide-menu')) {
    customElements.define('homepage-slide-menu', HomepageSlideMenu);
}
